import { useState, useEffect } from 'react';
import { calculateWaste, updateProjectStep3, getProject } from '../../services/api';

const Step3WasteStreams = ({ project, onComplete, onBack }) => {
  const [calculations, setCalculations] = useState(null);
  const [formData, setFormData] = useState({
    general_waste_override: '',
    recycling_override: '',
    organics_override: '',
    // Mixed use - residential
    residential_general_waste: '',
    residential_recycling: '',
    residential_organics: '',
    // Mixed use - commercial
    commercial_general_waste: '',
    commercial_recycling: '',
    commercial_organics: ''
  });
  const [isEditing, setIsEditing] = useState(false);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState('');
  const isMixedUse = project?.development_type === 'Mixed use';

  // Determine if this is a Medical Centre project (third stream = Clinical Waste)
  const isMedicalFacility = () => {
    const commercialOps = project?.step2_data?.commercial_operations || [];
    return commercialOps.some(op => {
      const opType = (op.operation_type || '').toLowerCase();
      return opType.includes('medical') || 
             opType.includes('health') ||
             opType.includes('hospital') ||
             opType.includes('healthcare');
    });
  };

  const getThirdStreamLabel = () => {
    return isMedicalFacility() ? "Clinical Waste" : "Organics / FOGO";
  };

  const getThirdStreamShortLabel = () => {
    return isMedicalFacility() ? "Clinical Waste" : "Organics";
  };

  useEffect(() => {
    loadCalculations();
  }, [project]);

  const loadCalculations = async () => {
    try {
      setLoading(true);
      const calc = await calculateWaste(project.id);
      setCalculations(calc);
      
      // Load existing overrides if they exist
      if (project?.step3_data) {
        if (isMixedUse) {
          setFormData({
            ...formData,
            residential_general_waste: project.step3_data.residential_general_waste || '',
            residential_recycling: project.step3_data.residential_recycling || '',
            residential_organics: project.step3_data.residential_organics || '',
            commercial_general_waste: project.step3_data.commercial_general_waste || '',
            commercial_recycling: project.step3_data.commercial_recycling || '',
            commercial_organics: project.step3_data.commercial_organics || ''
          });
          setIsEditing(
            !!project.step3_data.residential_general_waste ||
            !!project.step3_data.residential_recycling ||
            !!project.step3_data.residential_organics ||
            !!project.step3_data.commercial_general_waste ||
            !!project.step3_data.commercial_recycling ||
            !!project.step3_data.commercial_organics
          );
        } else {
          setFormData({
            ...formData,
            general_waste_override: project.step3_data.general_waste_override || '',
            recycling_override: project.step3_data.recycling_override || '',
            organics_override: project.step3_data.organics_override || ''
          });
          setIsEditing(
            !!project.step3_data.general_waste_override ||
            !!project.step3_data.recycling_override ||
            !!project.step3_data.organics_override
          );
        }
      }
    } catch (error) {
      console.error('Failed to calculate waste:', error);
      setError('Failed to calculate waste generation. Please ensure Step 2 is completed.');
    } finally {
      setLoading(false);
    }
  };

  const handleChange = (e) => {
    setFormData({
      ...formData,
      [e.target.name]: parseFloat(e.target.value) || ''
    });
  };

  const handleEdit = () => {
    if (!isEditing) {
      // Set current calculated values as defaults
      if (isMixedUse) {
        setFormData({
          ...formData,
          residential_general_waste: calculations?.residential_breakdown?.general_waste || '',
          residential_recycling: calculations?.residential_breakdown?.recycling || '',
          residential_organics: calculations?.residential_breakdown?.organics || '',
          commercial_general_waste: calculations?.commercial_breakdown?.general_waste || '',
          commercial_recycling: calculations?.commercial_breakdown?.recycling || '',
          commercial_organics: calculations?.commercial_breakdown?.organics || ''
        });
      } else {
        setFormData({
          ...formData,
          general_waste_override: calculations?.general_waste || '',
          recycling_override: calculations?.recycling || '',
          organics_override: calculations?.organics || ''
        });
      }
    }
    setIsEditing(!isEditing);
  };

  const handleResetToCalculated = () => {
    if (isMixedUse) {
      setFormData({
        ...formData,
        residential_general_waste: '',
        residential_recycling: '',
        residential_organics: '',
        commercial_general_waste: '',
        commercial_recycling: '',
        commercial_organics: ''
      });
    } else {
      setFormData({
        ...formData,
        general_waste_override: '',
        recycling_override: '',
        organics_override: ''
      });
    }
    setIsEditing(false);
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setError('');
    setSaving(true);

    try {
      const step3Data = {};
      
      // Only save overrides if user edited them
      if (isEditing) {
        if (isMixedUse) {
          // Save residential and commercial separately
          if (formData.residential_general_waste) {
            step3Data.residential_general_waste = parseFloat(formData.residential_general_waste);
          }
          if (formData.residential_recycling) {
            step3Data.residential_recycling = parseFloat(formData.residential_recycling);
          }
          if (formData.residential_organics) {
            step3Data.residential_organics = parseFloat(formData.residential_organics);
          }
          if (formData.commercial_general_waste) {
            step3Data.commercial_general_waste = parseFloat(formData.commercial_general_waste);
          }
          if (formData.commercial_recycling) {
            step3Data.commercial_recycling = parseFloat(formData.commercial_recycling);
          }
          if (formData.commercial_organics) {
            step3Data.commercial_organics = parseFloat(formData.commercial_organics);
          }
        } else {
          // Save combined overrides for non-mixed use
          if (formData.general_waste_override) {
            step3Data.general_waste_override = parseFloat(formData.general_waste_override);
          }
          if (formData.recycling_override) {
            step3Data.recycling_override = parseFloat(formData.recycling_override);
          }
          if (formData.organics_override) {
            step3Data.organics_override = parseFloat(formData.organics_override);
          }
        }
      }

      await updateProjectStep3(project.id, step3Data);
      
      const updatedProject = await getProject(project.id);
      onComplete(updatedProject);
    } catch (error) {
      console.error('Error saving step 3:', error);
      setError(error.response?.data?.detail || 'Failed to save waste streams. Please try again.');
    } finally {
      setSaving(false);
    }
  };

  const getFinalValue = (stream) => {
    if (!calculations) return 0;
    
    const overrideKey = `${stream}_override`;
    if (isEditing && formData[overrideKey]) {
      return formData[overrideKey];
    }
    
    return calculations[stream] || 0;
  };

  if (loading) {
    return (
      <div className="bg-white rounded-lg shadow p-8 text-center">
        <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-blue-600 mx-auto"></div>
        <p className="mt-4 text-gray-600">Calculating waste generation...</p>
      </div>
    );
  }

  if (!calculations || error) {
    return (
      <div className="bg-white rounded-lg shadow p-8 text-center">
        <div className="text-red-600 mb-4">
          <svg className="mx-auto h-12 w-12" fill="none" stroke="currentColor" viewBox="0 0 24 24">
            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 8v4m0 4h.01M21 12a9 9 0 11-18 0 9 9 0 0118 0z" />
          </svg>
        </div>
        <h2 className="text-xl font-bold text-gray-900 mb-2">Unable to Calculate Waste</h2>
        <p className="text-gray-600 mb-4">{error || 'Please complete Step 2 before proceeding.'}</p>
        <button
          onClick={onBack}
          className="px-4 py-2 bg-blue-600 text-white rounded-md hover:bg-blue-700"
        >
          Go Back to Step 2
        </button>
      </div>
    );
  }

  return (
    <div className="bg-white rounded-lg shadow">
      <div className="px-8 py-6 border-b border-gray-200">
        <div className="flex items-center justify-between mb-4">
          <button
            type="button"
            onClick={onBack}
            className="inline-flex items-center px-3 py-1.5 border border-gray-300 rounded-md text-sm font-medium text-gray-700 bg-white hover:bg-gray-50 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-blue-500"
          >
            <svg className="w-4 h-4 mr-1" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15 19l-7-7 7-7" />
            </svg>
            Back
          </button>
        </div>
        <h2 className="text-2xl font-bold text-gray-900">Step 3: Waste Streams</h2>
        <p className="mt-1 text-sm text-gray-600">
          Review calculated weekly waste generation. You can edit values if needed.
        </p>
      </div>

      <form onSubmit={handleSubmit} className="px-8 py-6 space-y-6">
        {error && (
          <div className="bg-red-50 border border-red-200 text-red-700 px-4 py-3 rounded">
            {error}
          </div>
        )}

        {/* Calculation Breakdown for Mixed Use */}
        {isMixedUse && calculations.residential_breakdown && calculations.commercial_breakdown && !isEditing && (
          <div className="bg-blue-50 border border-blue-200 rounded-lg p-4">
            <h3 className="text-sm font-medium text-blue-900 mb-2">Calculated Breakdown:</h3>
            <div className="grid grid-cols-2 gap-4 text-sm">
              <div>
                <p className="font-medium text-blue-800">Residential Component:</p>
                <p className="text-blue-700">General: {calculations.residential_breakdown.general_waste} L/week</p>
                <p className="text-blue-700">Recycling: {calculations.residential_breakdown.recycling} L/week</p>
                <p className="text-blue-700">{getThirdStreamShortLabel()}: {calculations.residential_breakdown.organics} L/week</p>
              </div>
              <div>
                <p className="font-medium text-blue-800">Commercial Component:</p>
                <p className="text-blue-700">General: {calculations.commercial_breakdown.general_waste} L/week</p>
                <p className="text-blue-700">Recycling: {calculations.commercial_breakdown.recycling} L/week</p>
                <p className="text-blue-700">{getThirdStreamShortLabel()}: {calculations.commercial_breakdown.organics} L/week</p>
              </div>
            </div>
          </div>
        )}

        {/* Waste Streams - MIXED USE */}
        {isMixedUse ? (
          <div className="space-y-6">
            <div className="flex justify-between items-center">
              <h3 className="text-lg font-medium text-gray-900">Weekly Waste Generation</h3>
              <div className="flex space-x-2">
                {isEditing && (
                  <button
                    type="button"
                    onClick={handleResetToCalculated}
                    className="px-3 py-1 text-sm border border-gray-300 rounded-md text-gray-700 hover:bg-gray-50"
                  >
                    Reset to Calculated
                  </button>
                )}
                <button
                  type="button"
                  onClick={handleEdit}
                  className="px-3 py-1 text-sm border border-blue-600 rounded-md text-blue-600 hover:bg-blue-50"
                >
                  {isEditing ? 'Cancel Edit' : 'Edit Values'}
                </button>
              </div>
            </div>

            {/* Residential Component */}
            <div className="border border-gray-300 rounded-lg p-4 bg-gray-50">
              <h4 className="text-md font-medium text-gray-900 mb-4">Residential Component</h4>
              <div className="space-y-3">
                <div>
                  <label className="block text-sm font-medium text-gray-700">
                    General Waste (Litres/week)
                  </label>
                  {isEditing ? (
                    <input
                      type="number"
                      name="residential_general_waste"
                      min="0"
                      step="0.01"
                      value={formData.residential_general_waste}
                      onChange={handleChange}
                      className="mt-1 block w-full px-3 py-2 border border-gray-300 rounded-md shadow-sm focus:outline-none focus:ring-blue-500 focus:border-blue-500 bg-white"
                    />
                  ) : (
                    <div className="mt-1 block w-full px-3 py-2 border border-gray-300 rounded-md bg-white text-gray-900 font-medium">
                      {(formData.residential_general_waste || calculations?.residential_breakdown?.general_waste || 0).toFixed(2)} L/week
                    </div>
                  )}
                </div>

                <div>
                  <label className="block text-sm font-medium text-gray-700">
                    Comingled Recycling (Litres/week)
                  </label>
                  {isEditing ? (
                    <input
                      type="number"
                      name="residential_recycling"
                      min="0"
                      step="0.01"
                      value={formData.residential_recycling}
                      onChange={handleChange}
                      className="mt-1 block w-full px-3 py-2 border border-gray-300 rounded-md shadow-sm focus:outline-none focus:ring-blue-500 focus:border-blue-500 bg-white"
                    />
                  ) : (
                    <div className="mt-1 block w-full px-3 py-2 border border-gray-300 rounded-md bg-white text-gray-900 font-medium">
                      {(formData.residential_recycling || calculations?.residential_breakdown?.recycling || 0).toFixed(2)} L/week
                    </div>
                  )}
                </div>

                <div>
                  <label className="block text-sm font-medium text-gray-700">
                    {getThirdStreamLabel()} (Litres/week)
                  </label>
                  {isEditing ? (
                    <input
                      type="number"
                      name="residential_organics"
                      min="0"
                      step="0.01"
                      value={formData.residential_organics}
                      onChange={handleChange}
                      className="mt-1 block w-full px-3 py-2 border border-gray-300 rounded-md shadow-sm focus:outline-none focus:ring-blue-500 focus:border-blue-500 bg-white"
                    />
                  ) : (
                    <div className="mt-1 block w-full px-3 py-2 border border-gray-300 rounded-md bg-white text-gray-900 font-medium">
                      {(formData.residential_organics || calculations?.residential_breakdown?.organics || 0).toFixed(2)} L/week
                    </div>
                  )}
                </div>
              </div>
            </div>

            {/* Commercial Component */}
            <div className="border border-gray-300 rounded-lg p-4 bg-gray-50">
              <h4 className="text-md font-medium text-gray-900 mb-4">Commercial Component</h4>
              <div className="space-y-3">
                <div>
                  <label className="block text-sm font-medium text-gray-700">
                    General Waste (Litres/week)
                  </label>
                  {isEditing ? (
                    <input
                      type="number"
                      name="commercial_general_waste"
                      min="0"
                      step="0.01"
                      value={formData.commercial_general_waste}
                      onChange={handleChange}
                      className="mt-1 block w-full px-3 py-2 border border-gray-300 rounded-md shadow-sm focus:outline-none focus:ring-blue-500 focus:border-blue-500 bg-white"
                    />
                  ) : (
                    <div className="mt-1 block w-full px-3 py-2 border border-gray-300 rounded-md bg-white text-gray-900 font-medium">
                      {(formData.commercial_general_waste || calculations?.commercial_breakdown?.general_waste || 0).toFixed(2)} L/week
                    </div>
                  )}
                </div>

                <div>
                  <label className="block text-sm font-medium text-gray-700">
                    Comingled Recycling (Litres/week)
                  </label>
                  {isEditing ? (
                    <input
                      type="number"
                      name="commercial_recycling"
                      min="0"
                      step="0.01"
                      value={formData.commercial_recycling}
                      onChange={handleChange}
                      className="mt-1 block w-full px-3 py-2 border border-gray-300 rounded-md shadow-sm focus:outline-none focus:ring-blue-500 focus:border-blue-500 bg-white"
                    />
                  ) : (
                    <div className="mt-1 block w-full px-3 py-2 border border-gray-300 rounded-md bg-white text-gray-900 font-medium">
                      {(formData.commercial_recycling || calculations?.commercial_breakdown?.recycling || 0).toFixed(2)} L/week
                    </div>
                  )}
                </div>

                <div>
                  <label className="block text-sm font-medium text-gray-700">
                    {getThirdStreamLabel()} (Litres/week)
                  </label>
                  {isEditing ? (
                    <input
                      type="number"
                      name="commercial_organics"
                      min="0"
                      step="0.01"
                      value={formData.commercial_organics}
                      onChange={handleChange}
                      className="mt-1 block w-full px-3 py-2 border border-gray-300 rounded-md shadow-sm focus:outline-none focus:ring-blue-500 focus:border-blue-500 bg-white"
                    />
                  ) : (
                    <div className="mt-1 block w-full px-3 py-2 border border-gray-300 rounded-md bg-white text-gray-900 font-medium">
                      {(formData.commercial_organics || calculations?.commercial_breakdown?.organics || 0).toFixed(2)} L/week
                    </div>
                  )}
                </div>
              </div>
            </div>

            {/* Combined Total for Mixed Use */}
            <div className="bg-blue-50 border-2 border-blue-300 rounded-lg p-4">
              <div className="space-y-2">
                <div className="flex justify-between items-center text-sm">
                  <span className="text-blue-900">Residential Total:</span>
                  <span className="font-medium text-blue-900">
                    {(
                      (parseFloat(formData.residential_general_waste) || calculations?.residential_breakdown?.general_waste || 0) +
                      (parseFloat(formData.residential_recycling) || calculations?.residential_breakdown?.recycling || 0) +
                      (parseFloat(formData.residential_organics) || calculations?.residential_breakdown?.organics || 0)
                    ).toFixed(2)} L/week
                  </span>
                </div>
                <div className="flex justify-between items-center text-sm">
                  <span className="text-blue-900">Commercial Total:</span>
                  <span className="font-medium text-blue-900">
                    {(
                      (parseFloat(formData.commercial_general_waste) || calculations?.commercial_breakdown?.general_waste || 0) +
                      (parseFloat(formData.commercial_recycling) || calculations?.commercial_breakdown?.recycling || 0) +
                      (parseFloat(formData.commercial_organics) || calculations?.commercial_breakdown?.organics || 0)
                    ).toFixed(2)} L/week
                  </span>
                </div>
                <div className="pt-2 border-t-2 border-blue-300 flex justify-between items-center">
                  <div>
                    <p className="text-sm font-medium text-blue-900">Combined Total Weekly Waste</p>
                    <p className="text-xs text-blue-700">Residential + Commercial</p>
                  </div>
                  <div className="text-2xl font-bold text-blue-900">
                    {(
                      (parseFloat(formData.residential_general_waste) || calculations?.residential_breakdown?.general_waste || 0) +
                      (parseFloat(formData.residential_recycling) || calculations?.residential_breakdown?.recycling || 0) +
                      (parseFloat(formData.residential_organics) || calculations?.residential_breakdown?.organics || 0) +
                      (parseFloat(formData.commercial_general_waste) || calculations?.commercial_breakdown?.general_waste || 0) +
                      (parseFloat(formData.commercial_recycling) || calculations?.commercial_breakdown?.recycling || 0) +
                      (parseFloat(formData.commercial_organics) || calculations?.commercial_breakdown?.organics || 0)
                    ).toFixed(2)} L/week
                  </div>
                </div>
              </div>
            </div>
          </div>
        ) : (
          /* Waste Streams - NON-MIXED USE */
          <div className="space-y-4">
          <div className="flex justify-between items-center">
            <h3 className="text-lg font-medium text-gray-900">Weekly Waste Generation</h3>
            <div className="flex space-x-2">
              {isEditing && (
                <button
                  type="button"
                  onClick={handleResetToCalculated}
                  className="px-3 py-1 text-sm border border-gray-300 rounded-md text-gray-700 hover:bg-gray-50"
                >
                  Reset to Calculated
                </button>
              )}
              <button
                type="button"
                onClick={handleEdit}
                className="px-3 py-1 text-sm border border-blue-600 rounded-md text-blue-600 hover:bg-blue-50"
              >
                {isEditing ? 'Cancel Edit' : 'Edit Values'}
              </button>
            </div>
          </div>

          {/* General Waste */}
          <div>
            <label htmlFor="general_waste" className="block text-sm font-medium text-gray-700">
              General Waste (Litres/week)
            </label>
            {isEditing ? (
              <input
                type="number"
                id="general_waste"
                name="general_waste_override"
                min="0"
                step="0.01"
                value={formData.general_waste_override}
                onChange={handleChange}
                className="mt-1 block w-full px-3 py-2 border border-gray-300 rounded-md shadow-sm focus:outline-none focus:ring-blue-500 focus:border-blue-500"
              />
            ) : (
              <div className="mt-1 block w-full px-3 py-2 border border-gray-300 rounded-md bg-gray-50 text-gray-900 font-medium">
                {getFinalValue('general_waste').toFixed(2)} L/week
              </div>
            )}
            {!isEditing && (
              <p className="mt-1 text-xs text-gray-500">
                Calculated based on {project.state_code} waste generation rates
              </p>
            )}
          </div>

          {/* Recycling */}
          <div>
            <label htmlFor="recycling" className="block text-sm font-medium text-gray-700">
              Comingled Recycling (Litres/week)
            </label>
            {isEditing ? (
              <input
                type="number"
                id="recycling"
                name="recycling_override"
                min="0"
                step="0.01"
                value={formData.recycling_override}
                onChange={handleChange}
                className="mt-1 block w-full px-3 py-2 border border-gray-300 rounded-md shadow-sm focus:outline-none focus:ring-blue-500 focus:border-blue-500"
              />
            ) : (
              <div className="mt-1 block w-full px-3 py-2 border border-gray-300 rounded-md bg-gray-50 text-gray-900 font-medium">
                {getFinalValue('recycling').toFixed(2)} L/week
              </div>
            )}
          </div>

          {/* Organics / Clinical Waste */}
          <div>
            <label htmlFor="organics" className="block text-sm font-medium text-gray-700">
              {getThirdStreamLabel()} (Litres/week)
            </label>
            {isEditing ? (
              <input
                type="number"
                id="organics"
                name="organics_override"
                min="0"
                step="0.01"
                value={formData.organics_override}
                onChange={handleChange}
                className="mt-1 block w-full px-3 py-2 border border-gray-300 rounded-md shadow-sm focus:outline-none focus:ring-blue-500 focus:border-blue-500"
              />
            ) : (
              <div className="mt-1 block w-full px-3 py-2 border border-gray-300 rounded-md bg-gray-50 text-gray-900 font-medium">
                {getFinalValue('organics').toFixed(2)} L/week
              </div>
            )}
            {getFinalValue('organics') === 0 && !isEditing && (
              <p className="mt-1 text-xs text-yellow-600">
                Note: {getThirdStreamShortLabel()} not specified for {project.state_code} residential rates
              </p>
            )}
          </div>
          </div>
        )}

        {/* Total - Only show for non-mixed use */}
        {!isMixedUse && (
          <div className="bg-blue-50 border-2 border-blue-300 rounded-lg p-4">
            <div className="flex justify-between items-center">
              <div>
                <p className="text-sm font-medium text-blue-900">Total Weekly Waste Generation</p>
                <p className="text-xs text-blue-700">All streams combined</p>
              </div>
              <div className="text-2xl font-bold text-blue-900">
                {(
                  getFinalValue('general_waste') +
                  getFinalValue('recycling') +
                  getFinalValue('organics')
                ).toFixed(2)} L/week
              </div>
            </div>
          </div>
        )}

        <div className="flex justify-between pt-6 border-t border-gray-200">
          <button
            type="button"
            onClick={onBack}
            className="px-4 py-2 border border-gray-300 rounded-md shadow-sm text-sm font-medium text-gray-700 bg-white hover:bg-gray-50 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-blue-500"
          >
            Back
          </button>
          <button
            type="submit"
            disabled={saving}
            className="px-6 py-2 border border-transparent rounded-md shadow-sm text-sm font-medium text-white bg-blue-600 hover:bg-blue-700 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-blue-500 disabled:opacity-50 disabled:cursor-not-allowed"
          >
            {saving ? 'Saving...' : 'Continue to General Waste Planning Guidance'}
          </button>
        </div>
      </form>
    </div>
  );
};

export default Step3WasteStreams;
